/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import ApiService from '@zola-helpers/client/dist/es/http/api';
import getEnvironment, { EnvironmentTypes } from '@zola-helpers/client/dist/es/util/environment';
import client from 'braintree-web/client';
import paypalCheckout from 'braintree-web/paypal-checkout';
import type { PayPalCheckoutLoadPayPalSDKOptions } from 'braintree-web/paypal-checkout';
import './paypal.less';

/*
 * It is important to centralize all zola paypal use cases here as the SDK now requires passing
 * dataAttributes on SDK load; which should only happen once, and only when the checkout amount
 * is final on the page.
 */

export const getBraintreeToken = (
  tokenUrl?: string
): Promise<{ data: { braintreeToken?: string } }> =>
  ApiService.get(tokenUrl || '/web-registry-api/v1/checkout/braintree/token');

const formatPrice = (amountCents: number): string => {
  const remainder = `${Math.round(amountCents % 100).toString()}0`;
  return `${Math.floor(amountCents / 100)}.${remainder.substr(0, 2)}`;
};

export interface FundingClientSet {
  paypalCheckoutInstance: any;
  paypalSdk: any;
  amountCents: number;
}

const usePayPalSDK = (
  amountCents: number,
  ppalNamespace = 'paypalSdk',
  tokenUrl?: string
): FundingClientSet => {
  const [fundingClientSet, setFundingClientSet] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading || (fundingClientSet && fundingClientSet.amountCents === amountCents)) {
        return;
      }

      setLoading(true);
      getBraintreeToken(tokenUrl)
        .then(({ data }: { data: { braintreeToken?: string } }) => data.braintreeToken)
        .then((braintreeToken: string | undefined) => {
          if (!braintreeToken) {
            return;
          }

          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          client
            .create({
              authorization: braintreeToken,
            })
            .then((clientInstance) =>
              paypalCheckout.create({
                client: clientInstance,
              })
            )
            .then(
              (instance) =>
                new Promise((resolve) => {
                  instance.loadPayPalSDK(
                    {
                      currency: 'USD',
                      'integration-date': '2021-11-01',
                      components: 'buttons,messages',
                      dataAttributes: {
                        'data-namespace': ppalNamespace,
                        amount: formatPrice(amountCents),
                      } as PayPalCheckoutLoadPayPalSDKOptions['dataAttributes'],
                    },
                    () => {
                      const paypalSdk = window[ppalNamespace as any] as unknown as any;

                      setLoading(false);
                      setFundingClientSet({
                        paypalCheckoutInstance: instance,
                        paypalSdk,
                        amountCents,
                      });
                      setTimeout(() => paypalSdk.Messages.render(), 50);
                      resolve(null);
                    }
                  );
                })
            );
        })
        .catch((e) => {
          throw e;
        });
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, fundingClientSet]);

  return fundingClientSet || {};
};

const PayInFourMessage = ({
  amountCents,
  tokenUrl,
  ppalNamespace,
}: {
  amountCents: number;
  tokenUrl?: string;
  ppalNamespace?: string;
}): JSX.Element | null => {
  const amount = formatPrice(amountCents);

  usePayPalSDK(amountCents, ppalNamespace || 'zola_paypal_messages', tokenUrl);

  return <div data-pp-message data-pp-placement="product" data-pp-amount={amount} />;
};

const Buttons = ({
  amountCents,
  id,
  onApprove,
  flow,
  showPayLater,
  tokenUrl,
  ppalNamespace,
}: {
  amountCents: number;
  id?: string;
  onApprove?: (data: any, actions: any, checkoutInstance: any) => void;
  flow?: string;
  showPayLater?: boolean;
  tokenUrl?: string;
  ppalNamespace?: string;
}): JSX.Element | null => {
  const payRef = useRef(null);
  const payLaterRef = useRef(null);

  const amount = formatPrice(amountCents);

  const { paypalCheckoutInstance, paypalSdk } = usePayPalSDK(
    amountCents,
    ppalNamespace || 'zola_paypal_buttons',
    tokenUrl
  );

  const divId = `zola-paypal-buttons-${id || '0'}`;
  const payButtonId = `${divId}-pay`;
  const payLaterButtonId = `${divId}-paylater`;

  useEffect(() => {
    if (
      !payRef.current ||
      !payLaterRef.current ||
      !(typeof amountCents === 'number') ||
      !paypalCheckoutInstance ||
      !paypalSdk?.version
    ) {
      return () => undefined;
    }

    const paypalSandbox = getEnvironment() !== EnvironmentTypes.PRODUCTION;

    const paypalParams = {
      env: paypalSandbox ? 'sandbox' : 'production',
      currency: 'USD',
      createOrder: () =>
        paypalCheckoutInstance.createPayment({
          flow: flow || 'checkout',
          amount,
          currency: 'USD',
          enableShippingAddress: false,
          shippingAddressEditable: false,
        }),
      onApprove: (data: any, actions: any) =>
        onApprove && onApprove(data, actions, paypalCheckoutInstance),
      onCancel: () => undefined, // Do nothing when user cancels payment flow
      onError: () => undefined, // Do nothing since there are no error messages on the cart modal
      style: {
        size: 'responsive', // allows custom sizing of paypal button via element styles attribute
        tagline: false,
        label: 'pay',
        height: 44, // height of button in pixels
        shape: 'pill',
      },
    };

    const payButton = paypalSdk.Buttons({
      fundingSource: paypalSdk.FUNDING.PAYPAL,
      ...paypalParams,
    });

    const payLaterButton = paypalSdk.Buttons({
      fundingSource: paypalSdk.FUNDING.PAYLATER,
      ...paypalParams,
    });

    const timer = setTimeout(() => {
      if (payRef.current && payButton.isEligible()) {
        payButton.render(`#${payButtonId}`);
      }
      if (payLaterRef.current && payLaterButton.isEligible()) {
        payLaterButton.render(`#${payLaterButtonId}`);
      }
    }, 500);

    return () => {
      payButton?.close();
      payLaterButton?.close();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divId, amount, paypalCheckoutInstance, paypalSdk, flow, onApprove]);

  return (
    <div className="zola-paypal-buttons-container">
      <div ref={payRef} id={payButtonId} className="zola-paypal-button" />
      <div
        ref={payLaterRef}
        id={payLaterButtonId}
        className={cx('zola-paypal-button', { 'hide-pay-in-four': !showPayLater })}
      />
      <div
        className={cx('zola-pay-in-four-message', { 'hide-pay-in-four': !showPayLater })}
        data-pp-message
        data-pp-placement="payment"
        data-pp-amount={amount}
      />
    </div>
  );
};

const PayPal = { usePayPalSDK, PayInFourMessage, Buttons };

export default PayPal;
